import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { onb } from "@/lib/onb";
import { useMhStream } from "@/lib/mh";
import { errText } from "@/lib/http";
import { useAuth } from "@/context/AuthContext";
import { Eyebrow } from "@/components/mh/bits";
import { Evidence } from "@/components/onb/Evidence";
import { Interviews } from "@/components/onb/Interviews";
import { Profile } from "@/components/onb/Profile";
import { TailorPanel } from "@/components/onb/TailorPanel";

const STEPS = [["evidence", "1 · Evidence"], ["interviews", "2 · Interviews"], ["profile", "3 · Profile"], ["tailor", "4 · Tailor"]];

export default function BuildMap() {
  const { canManage, isOwner, current } = useAuth();
  const [st, setSt] = useState(null);
  const [step, setStep] = useState("evidence");
  const load = useCallback(() => onb.state().then((r) => setSt(r.data)).catch((e) => toast.error(errText(e))), []);
  useEffect(() => { load(); }, [load]);
  useMhStream((ev) => {
    if (ev.type === "tailor.progress") setSt((s) => s && { ...s, job: ev.job });
    if (ev.type === "onb.interview") load();
  });
  if (!st) return <div className="h-[50vh] grid place-items-center font-mono text-xs text-muted-foreground">Opening your map…</div>;
  const done = { evidence: st.sources.length > 0 && st.profile.draft_confirmed, interviews: st.interviews.departments.some((d) => d.status === "done"), profile: false, tailor: ["done", "partial"].includes(st.job?.status) };
  return (
    <div className="max-w-[1300px] mx-auto space-y-6" data-testid="build-map-page">
      <header className="mh-rise">
        <Eyebrow>{current?.name} · build your map</Eyebrow>
        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-3">Your AI workforce, <em className="text-muted-foreground">in your own words.</em></h1>
        <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl">We read your business, interview your team, and rebuild all 7 departments and 140 roles around how you actually work, ranked by what each one is worth.</p>
      </header>
      <nav className="flex gap-1 overflow-x-auto p-1 rounded-full bg-white/[0.04] border border-white/10 w-fit max-w-full" data-testid="onb-steps">
        {STEPS.map(([k, t]) => (
          <button key={k} onClick={() => setStep(k)} data-testid={`onb-step-${k}`}
            className={`h-9 px-4 rounded-full text-xs whitespace-nowrap transition-colors ${step === k ? "bg-white text-black" : "text-muted-foreground hover:text-foreground"}`}>
            {t}{done[k] ? " ✓" : ""}</button>
        ))}
      </nav>
      {step === "evidence" && <Evidence st={st} reload={load} canEdit={canManage} />}
      {step === "interviews" && <Interviews st={st} reload={load} canEdit={canManage} isOwner={isOwner} />}
      {step === "profile" && <Profile st={st} reload={load} canEdit={canManage} />}
      {step === "tailor" && <TailorPanel st={st} canEdit={canManage} />}
    </div>
  );
}
